import type { ErrorInfo, FC, ReactNode } from 'react'
import { Component } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Button, Typography } from '@mui/material'
import { Refresh } from '@mui/icons-material'

interface ErrorBoundaryProps {
  readonly children: ReactNode
  readonly fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ error, errorInfo })
    logError(error, errorInfo)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  override render(): ReactNode {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (!hasError) {
      return children
    }

    if (fallback) {
      return fallback
    }

    return (
      <ErrorFallback
        errorMessage={error?.message ?? null}
        errorStack={error?.stack ?? null}
        componentStack={errorInfo?.componentStack ?? null}
        onRetry={this.handleRetry}
      />
    )
  }
}

function logError(error: Error, errorInfo: ErrorInfo): void {
  if (import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    return
  }

  // eslint-disable-next-line no-console
  console.group('ErrorBoundary caught an error')
  // eslint-disable-next-line no-console
  console.error('Error:', error)
  // eslint-disable-next-line no-console
  console.error('Error Info:', errorInfo)
  // eslint-disable-next-line no-console
  console.error('Component Stack:', errorInfo.componentStack)
  // eslint-disable-next-line no-console
  console.groupEnd()
}

interface ErrorFallbackProps {
  readonly errorMessage: string | null
  readonly errorStack: string | null
  readonly componentStack: string | null
  readonly onRetry: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
const ErrorFallback: FC<ErrorFallbackProps> = ({
  errorMessage,
  errorStack,
  componentStack,
  onRetry,
}) => {
  const isDevelopment = import.meta.env.DEV
  const showDetails = isDevelopment && (errorStack || componentStack)

  return (
    <Box sx={containerSx}>
      <Alert severity="error" sx={alertSx}>
        <Typography variant="h6" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {errorMessage ?? 'An unexpected error occurred'}
        </Typography>
      </Alert>

      <Button variant="contained" startIcon={<Refresh />} onClick={onRetry} sx={retryButtonSx}>
        Try Again
      </Button>

      {showDetails && (
        <Box component="details" sx={detailsSx}>
          <Typography component="summary" variant="body2" sx={summarySx}>
            Error Details (Development Only)
          </Typography>
          <Typography component="pre" variant="caption" sx={stackTraceSx}>
            {errorStack}
            {componentStack && `\n\nComponent Stack:${componentStack}`}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
  p: 3,
  textAlign: 'center',
}

const alertSx: SxProps<Theme> = {
  mb: 3,
  width: '100%',
  maxWidth: 600,
}

const retryButtonSx: SxProps<Theme> = {
  mt: 2,
}

const detailsSx: SxProps<Theme> = {
  mt: 3,
  p: 2,
  bgcolor: 'action.hover',
  borderRadius: 1,
  width: '100%',
  maxWidth: 800,
  overflow: 'auto',
}

const summarySx: SxProps<Theme> = {
  cursor: 'pointer',
  fontWeight: 600,
}

const stackTraceSx: SxProps<Theme> = {
  mt: 1,
  fontSize: '0.75rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
}
